import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/authService';
import { userService, UserWithGrower, CreateUserData, UpdateUserData } from '../services/userService';
import { growerService } from '../services/growerService';
import Layout from '../components/Layout';
import { User, Grower } from '../types';
import { SUPPORTED_LANGUAGES } from '../i18n';

const Users: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [users, setUsers] = useState<UserWithGrower[]>([]);
  const [growers, setGrowers] = useState<Grower[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserWithGrower | null>(null);
  const [formData, setFormData] = useState<{
    email: string;
    password: string;
    full_name: string;
    is_admin: boolean;
    is_admin_grower: boolean;
    is_active: boolean;
    grower_ids: number[];
    language: string;
  }>({
    email: '',
    password: '',
    full_name: '',
    is_admin: false,
    is_admin_grower: false,
    is_active: true,
    grower_ids: [],
    language: 'en',
  });
  const [error, setError] = useState('');

  // Filter & search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<'all' | 'admin' | 'admin_grower' | 'grower'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [filterGrower, setFilterGrower] = useState<string>('all');

  // Pagination state
  const [userPage, setUserPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(25);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!currentUser?.is_admin && !currentUser?.is_admin_grower) return;

    const refreshInterval = window.setInterval(async () => {
      try {
        setUsers(await userService.getUsers());
      } catch (err) {
        console.error('Failed to refresh user presence:', err);
      }
    }, 60_000);
    return () => window.clearInterval(refreshInterval);
  }, [currentUser?.is_admin, currentUser?.is_admin_grower]);

  const loadData = async () => {
    try {
      const user = await authService.getCurrentUser();
      setCurrentUser(user);

      const [usersData, growersData] = await Promise.all([
        userService.getUsers(),
        growerService.getGrowers(),
      ]);
      setUsers(usersData);
      setGrowers(growersData);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user?: UserWithGrower) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '',
        full_name: user.full_name || '',
        is_admin: user.is_admin,
        is_admin_grower: user.is_admin_grower || false,
        is_active: user.is_active,
        grower_ids: user.grower_ids || [],
        language: user.language || 'en',
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        full_name: '',
        is_admin: false,
        is_admin_grower: false,
        is_active: true,
        // Grower admins: pre-select their own managed growers so the user is assigned immediately
        grower_ids: !currentUser?.is_admin && currentUser?.is_admin_grower
          ? (currentUser?.grower_ids || [])
          : [],
        language: 'en',
      });
    }
    setShowModal(true);
    setError('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      full_name: '',
      is_admin: false,
      is_admin_grower: false,
      is_active: true,
      grower_ids: [],
      language: 'en',
    });
    setError('');
  };

  const handleGrowerToggle = (growerId: number) => {
    setFormData((prev) => ({
      ...prev,
      grower_ids: prev.grower_ids.includes(growerId)
        ? prev.grower_ids.filter((id) => id !== growerId)
        : [...prev.grower_ids, growerId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Grower admins must assign at least one grower when creating a user
    if (!currentUser?.is_admin && currentUser?.is_admin_grower && !editingUser) {
      if (formData.grower_ids.length === 0) {
        setError('You must assign the user to at least one of your managed growers');
        return;
      }
    }

    try {
      if (editingUser) {
        const updateData: UpdateUserData = {
          email: formData.email,
          full_name: formData.full_name || undefined,
          is_admin: formData.is_admin,
          is_admin_grower: formData.is_admin_grower,
          is_active: formData.is_active,
          grower_ids: formData.grower_ids,
          language: formData.language,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await userService.updateUser(editingUser.id, updateData);
      } else {
        if (!formData.password) {
          setError('Password is required for new users');
          return;
        }
        const createData: CreateUserData = {
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name || undefined,
          is_admin: formData.is_admin,
          is_admin_grower: formData.is_admin_grower,
          grower_ids: formData.grower_ids,
        };
        await userService.createUser(createData);
      }
      handleCloseModal();
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await userService.deleteUser(id);
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleSendPasswordReset = async (user: UserWithGrower) => {
    if (!window.confirm(`Send a password reset email to ${user.email}?`)) {
      return;
    }

    try {
      const result = await userService.sendPasswordReset(user.id);
      alert(result.message);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to send password reset email');
    }
  };

  // Derived filtered list
  const filteredUsers = users.filter(user => {
    const lower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm ||
      (user.full_name || '').toLowerCase().includes(lower) ||
      user.email.toLowerCase().includes(lower);
    const matchesRole =
      filterRole === 'all' ||
      (filterRole === 'admin' && user.is_admin) ||
      (filterRole === 'admin_grower' && !user.is_admin && user.is_admin_grower) ||
      (filterRole === 'grower' && !user.is_admin && !user.is_admin_grower);
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && user.is_active) ||
      (filterStatus === 'inactive' && !user.is_active);
    const matchesGrower =
      filterGrower === 'all' ||
      (filterGrower === 'unassigned' && (!user.grower_ids || user.grower_ids.length === 0)) ||
      (user.grower_ids || []).map(String).includes(filterGrower);
    return matchesSearch && matchesRole && matchesStatus && matchesGrower;
  });

  const userTotalPages = Math.max(1, Math.ceil(filteredUsers.length / usersPerPage));
  const paginatedUsers = filteredUsers.slice((userPage - 1) * usersPerPage, userPage * usersPerPage);

  const clearFilters = () => {
    setSearchTerm('');
    setFilterRole('all');
    setFilterStatus('all');
    setFilterGrower('all');
    setUserPage(1);
  };

  const formatLoginTime = (value: string | null) => {
    if (!value) return t('users.neverLoggedIn');
    return new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  };

  if (loading) {
    return (
      <Layout user={currentUser || undefined}>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">{t('common.loading')}</div>
        </div>
      </Layout>
    );
  }

  // Allow both system admins and grower admins
  if (!currentUser?.is_admin && !currentUser?.is_admin_grower) {
    return (
      <Layout user={currentUser || undefined}>
        <div className="py-6">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {t('common.noPermission')}
          </div>
        </div>
      </Layout>
    );
  }

  const isSystemAdmin = currentUser?.is_admin === true;

  return (
    <Layout user={currentUser || undefined}>
      <div className="py-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-900">{t('users.title')}</h2>
          <button
            onClick={() => handleOpenModal()}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
          >
            {t('users.addUser')}
          </button>
        </div>

        {users.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-gray-500">{t('users.noUsers')}</p>
          </div>
        ) : (
          <>
            {/* Search & Filter Bar */}
            <div className="bg-white shadow rounded-lg p-4 mb-4 flex flex-wrap gap-3 items-end">
              {/* Search */}
              <div className="flex-1 min-w-[180px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
                <input
                  type="text"
                  placeholder="Name or email…"
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setUserPage(1); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              {/* Role filter */}
              <div className="min-w-[140px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">Role</label>
                <select
                  value={filterRole}
                  onChange={e => { setFilterRole(e.target.value as any); setUserPage(1); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All roles</option>
                  <option value="admin">Admin</option>
                  <option value="admin_grower">Grower Admin</option>
                  <option value="grower">Grower</option>
                </select>
              </div>
              {/* Status filter */}
              <div className="min-w-[130px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                <select
                  value={filterStatus}
                  onChange={e => { setFilterStatus(e.target.value as any); setUserPage(1); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              {/* Grower filter */}
              {growers.length > 0 && (
                <div className="min-w-[160px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Grower</label>
                  <select
                    value={filterGrower}
                    onChange={e => { setFilterGrower(e.target.value); setUserPage(1); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="all">All growers</option>
                    <option value="unassigned">Unassigned</option>
                    {growers.map(g => (
                      <option key={g.id} value={String(g.id)}>{g.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {/* Summary + clear */}
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>
                  {filteredUsers.length < users.length
                    ? `${filteredUsers.length} filtered`
                    : `${users.length} user${users.length !== 1 ? 's' : ''}`}
                </span>
                {(searchTerm || filterRole !== 'all' || filterStatus !== 'all' || filterGrower !== 'all') && (
                  <button onClick={clearFilters} className="text-primary-600 hover:text-primary-700 text-xs font-medium">
                    Clear filters
                  </button>
                )}
              </div>
            </div>

            {filteredUsers.length === 0 ? (
              <div className="bg-white shadow rounded-lg p-6 text-center">
                <p className="text-gray-500 mb-2">No users match your search criteria.</p>
                <button onClick={clearFilters} className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                  Clear filters
                </button>
              </div>
            ) : (
              <>
                {/* Pagination helper */}
                {(() => {
                  const PaginationBar = ({ position }: { position: 'top' | 'bottom' }) => (
                    userTotalPages > 1 ? (
                      <div className={`flex items-center justify-between px-4 py-3 bg-white border-gray-200 ${position === 'top' ? 'border border-b-0 rounded-t-lg' : 'border border-t-0 rounded-b-lg'}`}>
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                          <span>
                            Showing{' '}
                            <span className="font-medium">{(userPage - 1) * usersPerPage + 1}</span>
                            {' '}–{' '}
                            <span className="font-medium">{Math.min(userPage * usersPerPage, filteredUsers.length)}</span>
                            {' '}of{' '}
                            <span className="font-medium">{filteredUsers.length}</span> users
                          </span>
                          <label className="flex items-center gap-1.5">
                            Per page:
                            <select
                              value={usersPerPage}
                              onChange={e => { setUsersPerPage(Number(e.target.value)); setUserPage(1); }}
                              className="border border-gray-300 rounded-md text-sm px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
                            >
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                          </label>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => setUserPage(1)} disabled={userPage === 1} className="px-2 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50 disabled:cursor-not-allowed" title="First page">«</button>
                          <button onClick={() => setUserPage(p => Math.max(1, p - 1))} disabled={userPage === 1} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50 disabled:cursor-not-allowed">Previous</button>
                          {Array.from({ length: userTotalPages }, (_, i) => i + 1)
                            .filter(p => p === 1 || p === userTotalPages || Math.abs(p - userPage) <= 2)
                            .reduce<(number | 'gap')[]>((acc, p, idx, arr) => {
                              if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('gap');
                              acc.push(p);
                              return acc;
                            }, [])
                            .map((p, idx) =>
                              p === 'gap' ? (
                                <span key={`gap-${position}-${idx}`} className="px-2 py-1.5 text-sm text-gray-400">…</span>
                              ) : (
                                <button
                                  key={`${position}-${p}`}
                                  onClick={() => setUserPage(p as number)}
                                  className={`px-3 py-1.5 text-sm border rounded-md ${userPage === p ? 'bg-primary-600 text-white border-primary-600 font-medium' : 'border-gray-300 hover:bg-gray-50'}`}
                                >{p}</button>
                              )
                            )}
                          <button onClick={() => setUserPage(p => Math.min(userTotalPages, p + 1))} disabled={userPage === userTotalPages} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50 disabled:cursor-not-allowed">Next</button>
                          <button onClick={() => setUserPage(userTotalPages)} disabled={userPage === userTotalPages} className="px-2 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50 disabled:cursor-not-allowed" title="Last page">»</button>
                        </div>
                      </div>
                    ) : null
                  );
                  return (
                    <>
                      <PaginationBar position="top" />
                      {/* Desktop Table View */}
                      <div className="hidden lg:block bg-white shadow sm:rounded-none overflow-hidden border border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('users.fullName')}</th>
                              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.email')}</th>
                              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('users.role')}</th>
                              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('users.assignedGrowers')}</th>
                              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('users.language')}</th>
                              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.status')}</th>
                              {isSystemAdmin && (
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('users.loginStatus')}</th>
                              )}
                              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.actions')}</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedUsers.map((user) => (
                              <tr key={user.id} className="hover:bg-gray-50">
                                <td className="px-3 py-3 text-sm font-medium text-gray-900 max-w-[150px] truncate" title={user.full_name || '-'}>{user.full_name || '-'}</td>
                                <td className="px-3 py-3 text-sm text-gray-900 max-w-[200px] break-words">{user.email}</td>
                                <td className="px-3 py-3 whitespace-nowrap">
                                  <span className={`px-2 py-1 text-xs rounded-full inline-block ${user.is_admin ? 'bg-purple-100 text-purple-800' : user.is_admin_grower ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                    {user.is_admin ? t('common.admin') : user.is_admin_grower ? 'Grower Admin' : t('nav.growers')}
                                  </span>
                                </td>
                                <td className="px-3 py-3 text-sm text-gray-500">
                                  {user.grower_names && user.grower_names.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {user.grower_names.map((name, idx) => (
                                        <span key={idx} className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">{name}</span>
                                      ))}
                                    </div>
                                  ) : <span className="text-gray-400">-</span>}
                                </td>
                                <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-500 text-center">{(user.language || 'en').toUpperCase()}</td>
                                <td className="px-2 py-3 whitespace-nowrap">
                                  <span className={`px-2 py-1 text-xs rounded-full inline-block ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                    {user.is_active ? t('common.active') : t('common.inactive')}
                                  </span>
                                </td>
                                {isSystemAdmin && (
                                  <td className="px-3 py-3 whitespace-nowrap text-sm">
                                    <div className="flex items-center gap-2">
                                      <span className={`h-2.5 w-2.5 rounded-full ${user.is_logged_in ? 'bg-green-500' : 'bg-gray-300'}`} aria-hidden="true" />
                                      <span className={user.is_logged_in ? 'font-medium text-green-700' : 'text-gray-500'}>
                                        {user.is_logged_in ? t('users.online') : t('users.offline')}
                                      </span>
                                    </div>
                                    <div className="mt-1 text-xs text-gray-500" title={user.last_login_at || undefined}>
                                      {t('users.lastLogin')}: {formatLoginTime(user.last_login_at)}
                                    </div>
                                  </td>
                                )}
                                <td className="px-3 py-3 whitespace-nowrap text-sm">
                                  <div className="flex flex-col gap-1">
                                    <button onClick={() => handleOpenModal(user)} className="text-blue-600 hover:text-blue-900 text-left">{t('common.edit')}</button>
                                    {isSystemAdmin && (
                                      <button onClick={() => handleSendPasswordReset(user)} className="text-yellow-600 hover:text-yellow-900 text-left">{t('users.sendPasswordReset')}</button>
                                    )}
                                    {isSystemAdmin && user.id !== currentUser?.id && (
                                      <button onClick={() => handleDelete(user.id)} className="text-red-600 hover:text-red-900 text-left">{t('common.delete')}</button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile/Tablet Card View */}
                      <div className="lg:hidden space-y-4">
                        {paginatedUsers.map((user) => (
                          <div key={user.id} className="bg-white shadow rounded-lg p-4 space-y-3">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h3 className="text-sm font-semibold text-gray-900">{user.full_name || '-'}</h3>
                                <p className="text-sm text-gray-600 break-words">{user.email}</p>
                              </div>
                              <div className="flex gap-2 ml-2">
                                <span className={`px-2 py-1 text-xs rounded-full ${user.is_admin ? 'bg-purple-100 text-purple-800' : user.is_admin_grower ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                  {user.is_admin ? t('common.admin') : user.is_admin_grower ? 'Grower Admin' : t('nav.growers')}
                                </span>
                                <span className={`px-2 py-1 text-xs rounded-full ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                  {user.is_active ? t('common.active') : t('common.inactive')}
                                </span>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-gray-500">{t('users.language')}:</span>
                                <span className="ml-1 text-gray-900">{(user.language || 'en').toUpperCase()}</span>
                              </div>
                              {isSystemAdmin && (
                                <div>
                                  <span className="text-gray-500">{t('users.loginStatus')}:</span>
                                  <span className={`ml-1 ${user.is_logged_in ? 'font-medium text-green-700' : 'text-gray-900'}`}>
                                    {user.is_logged_in ? t('users.online') : t('users.offline')}
                                  </span>
                                </div>
                              )}
                            </div>
                            {isSystemAdmin && (
                              <div className="text-sm">
                                <span className="text-gray-500">{t('users.lastLogin')}:</span>
                                <span className="ml-1 text-gray-900">{formatLoginTime(user.last_login_at)}</span>
                              </div>
                            )}
                            {user.grower_names && user.grower_names.length > 0 && (
                              <div className="text-sm">
                                <span className="text-gray-500">{t('users.assignedGrowers')}:</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {user.grower_names.map((name, idx) => (
                                    <span key={idx} className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">{name}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div className="flex flex-wrap gap-2 pt-2 border-t">
                              <button onClick={() => handleOpenModal(user)} className="text-sm text-blue-600 hover:text-blue-900">{t('common.edit')}</button>
                              {isSystemAdmin && (
                                <button onClick={() => handleSendPasswordReset(user)} className="text-sm text-yellow-600 hover:text-yellow-900">{t('users.sendPasswordReset')}</button>
                              )}
                              {isSystemAdmin && user.id !== currentUser?.id && (
                                <button onClick={() => handleDelete(user.id)} className="text-sm text-red-600 hover:text-red-900">{t('common.delete')}</button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      <PaginationBar position="bottom" />
                    </>
                  );
                })()}
              </>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-4 sm:mx-auto p-5 border w-auto max-w-sm sm:max-w-md shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingUser ? t('users.editUser') : t('users.addUser')}
            </h3>
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.email')} *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.password')} {editingUser ? `(${t('users.newPassword')})` : '*'}
                </label>
                <input
                  type="password"
                  required={!editingUser}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('users.fullName')}
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('users.language')}
                </label>
                <select
                  value={formData.language}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('users.assignedGrowers')}
                  {!isSystemAdmin && <span className="text-red-500 ml-1">*</span>}
                </label>
                {growers.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">No growers available</p>
                ) : (
                  <div className="border border-gray-300 rounded-md max-h-48 overflow-y-auto p-2 space-y-1">
                    {growers.map((grower) => (
                      <label key={grower.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={formData.grower_ids.includes(grower.id)}
                          onChange={() => handleGrowerToggle(grower.id)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">
                          {grower.name}
                          {grower.parent_id && (
                            <span className="ml-1 text-xs text-gray-400">(sub-grower)</span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {isSystemAdmin
                    ? 'Select one or more growers. Assigning a parent grower grants access to all its sub-growers.'
                    : 'Select which of your growers this user should be assigned to.'}
                </p>
              </div>
              <div className="mb-4 flex items-center space-x-6">
                {isSystemAdmin && (
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_admin}
                      onChange={(e) => setFormData({
                        ...formData,
                        is_admin: e.target.checked,
                        is_admin_grower: e.target.checked ? false : formData.is_admin_grower,
                      })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{t('users.isAdmin')}</span>
                  </label>
                )}
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_admin_grower}
                    disabled={formData.is_admin}
                    onChange={(e) => setFormData({ ...formData, is_admin_grower: e.target.checked })}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">Grower Admin</span>
                </label>
                {editingUser && (
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{t('users.isActive')}</span>
                  </label>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                  {editingUser ? t('common.save') : t('users.addUser')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Users;
